import { combineEpics, ofType } from 'redux-observable'
import { from, of, zip } from 'rxjs'
import { catchError, filter, map, switchMap, takeUntil } from 'rxjs/operators'

import { URL_CONST } from 'utils/urlConsts'
import { matchURL } from 'utils/urls'

import {
  selectActualCompanyId,
  selectHasSelectedCompanyFilterAccess,
} from 'containers/App/selectors'
import { FETCH_SELECTED_COMPANY_DATA_ASYNC } from 'containers/PartnerOptions/constants'

import { DATE_FILTER_CHANGE, SCANBACK_CHANGE } from '../constants'
import {
  selectDateFilter,
  selectDateRange,
  selectHasInsightWidgetAccess,
  selectWithoutScanback,
} from '../selectors'

import { INSIGHT_THREAT_TYPES_REQUEST_ASYNC, INSIGHT_WIDGET_REQUEST_ASYNC } from './constants'

const commonParams = {
  email_widget_type: ['Presumed phishing', 'Presumed spam'],
  classification: ['Phishing', 'Spam', 'Unclassified'],
  page_number: 1,
  page_size: 10000,
  logDateRange: null,
  log_type: [
    'Suspicious email detected',
    'Suspicious email reported',
    'Email ignored (allow list)',
    'Classified as Phishing',
    'Classified as Spam',
    'Classified as Safe',
    'Reclassified as Phishing',
    'Reclassified as Spam',
    'Reclassified as Safe',
    'Revert to Phishing',
    'Revert to Spam',
    'Revert to Safe',
  ],
}

const getInsightsWidgetEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS,
      INSIGHT_WIDGET_REQUEST_ASYNC.INITIATE,
      DATE_FILTER_CHANGE,
      SCANBACK_CHANGE
    ),
    filter((action) => {
      if (action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS)
        return matchURL(URL_CONST.dashboard.home)
      return true
    }),
    switchMap(() => {
      const state = state$.value
      // companyFilter 403s unless the user can swap into the selected company.
      if (!selectHasSelectedCompanyFilterAccess(state))
        return of(INSIGHT_WIDGET_REQUEST_ASYNC.cancel())
      // The backend 403s without irontraps_viewer; reset the loader instead of firing.
      if (!selectHasInsightWidgetAccess(state)) return of(INSIGHT_WIDGET_REQUEST_ASYNC.cancel())
      const companyId = selectActualCompanyId(state)
      const feDateRange = selectDateRange(state)
      const dateFilter = selectDateFilter(state)
      const emailDateRange = selectDateRange(state)
      const withoutScanback = selectWithoutScanback(state)

      const baseParams = {
        ...commonParams,
        feDateRange,
        emailDateRange,
        withoutScanback,
        company_ids: [companyId],
        custom_dates: Array.isArray(dateFilter),
      }

      return zip(
        api.dashboard.getInsightsDetection(companyId, baseParams),
        api.dashboard.getInsightsVip(companyId, baseParams)
      ).pipe(
        map((responses) => {
          const combinedResponse = responses.reduce(
            (acc, response) => ({ ...acc, ...response.response }),
            {}
          )
          return INSIGHT_WIDGET_REQUEST_ASYNC.onSuccess(combinedResponse)
        }),
        catchError((error) => of(epicsErrorHandler(INSIGHT_WIDGET_REQUEST_ASYNC.onError, error))),
        takeUntil(
          action$.pipe(
            ofType(INSIGHT_WIDGET_REQUEST_ASYNC.CANCEL, INSIGHT_WIDGET_REQUEST_ASYNC.ERROR)
          )
        )
      )
    })
  )

const getInsightsThreatTypesEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS,
      INSIGHT_THREAT_TYPES_REQUEST_ASYNC.INITIATE,
      DATE_FILTER_CHANGE,
      SCANBACK_CHANGE
    ),
    filter((action) => {
      if (action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS)
        return matchURL(URL_CONST.dashboard.home)
      return true
    }),
    switchMap(() => {
      const state = state$.value
      // companyFilter 403s unless the user can swap into the selected company.
      if (!selectHasSelectedCompanyFilterAccess(state))
        return of(INSIGHT_THREAT_TYPES_REQUEST_ASYNC.cancel())
      // The backend 403s without irontraps_viewer; reset the loader instead of firing.
      if (!selectHasInsightWidgetAccess(state))
        return of(INSIGHT_THREAT_TYPES_REQUEST_ASYNC.cancel())
      const companyId = selectActualCompanyId(state)
      const feDateRange = selectDateRange(state)
      const dateFilter = selectDateFilter(state)
      const emailDateRange = selectDateRange(state)
      const withoutScanback = selectWithoutScanback(state)

      const baseParams = {
        ...commonParams,
        feDateRange,
        emailDateRange,
        withoutScanback,
        company_ids: [companyId],
        custom_dates: Array.isArray(dateFilter),
      }

      return from(api.dashboard.getInsightsThreatTypes(companyId, baseParams)).pipe(
        map((responses) => INSIGHT_THREAT_TYPES_REQUEST_ASYNC.onSuccess(responses.response)),
        catchError((error) =>
          of(epicsErrorHandler(INSIGHT_THREAT_TYPES_REQUEST_ASYNC.onError, error))
        ),
        takeUntil(
          action$.pipe(
            ofType(
              INSIGHT_THREAT_TYPES_REQUEST_ASYNC.CANCEL,
              INSIGHT_THREAT_TYPES_REQUEST_ASYNC.ERROR
            )
          )
        )
      )
    })
  )

export default combineEpics(getInsightsWidgetEpic, getInsightsThreatTypesEpic)
